/**
 * Prompt Packs page - Create and manage prompt packs
 * Workflow 8.5 from CLAUDE.md
 */
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import PublishIcon from '@mui/icons-material/Publish';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { packsApi, promptsApi } from '../../services/api';
import ImportPromptPackDialog from '../../components/ImportPromptPackDialog';
import PublishPromptPackDialog from '../../components/PublishPromptPackDialog';

interface Pack {
  entityId: string;
  promptPackName: string;
  description: string;
  price: number;
  currency: string;
  userPromptIds: string[];
  workflowIds: string[];
  freeSampleIds: string[];
  isPublic: boolean;
  tags: string[];
  createdDate: string;
  modifiedDate: string;
}

interface Prompt {
  entityId: string;
  promptName: string;
  description: string;
}

function shortId(value: string): string {
  return value.length > 8 ? value.slice(0, 8) : value;
}

export default function PacksPage() {
  const location = useLocation();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [includePublic, setIncludePublic] = useState(false);
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [contentsDialogOpen, setContentsDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [packToPublish, setPackToPublish] = useState<Pack | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPack, setMenuPack] = useState<Pack | null>(null);
  const [showImportBanner, setShowImportBanner] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const importedBookId = searchParams.get('bookId');
  const importedPackId = searchParams.get('packId');
  const importedProjectId = searchParams.get('projectId');
  const importedPackCount = searchParams.get('packCount');

  // New pack form state
  const [newPack, setNewPack] = useState({
    name: '',
    description: '',
    price: 0,
    tags: '',
    isPublic: false,
    selectedPromptIds: [] as string[],
    freeSampleIds: [] as string[],
  });

  useEffect(() => {
    const fetchPacks = async () => {
      setLoading(true);
      setError(null);
      try {
        const [packsResult, promptsResult] = await Promise.all([
          packsApi.list(includePublic),
          promptsApi.list(false),
        ]);
        setPacks(packsResult.data || []);
        setPrompts(promptsResult.data || []);
      } catch (err) {
        setError('Failed to load packs');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, [includePublic]);

  const filteredPacks = packs.filter((p) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.promptPackName.toLowerCase().includes(searchLower) ||
      p.description.toLowerCase().includes(searchLower) ||
      (p.tags ?? []).some((t) => t.toLowerCase().includes(searchLower))
    );
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, pack: Pack) => {
    setMenuAnchor(event.currentTarget);
    setMenuPack(pack);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuPack(null);
  };

  const handleViewDetails = (pack: Pack) => {
    setSelectedPack(pack);
    setDialogOpen(true);
  };

  const handleViewContents = (pack: Pack) => {
    setSelectedPack(pack);
    setContentsDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestDelete = (pack: Pack) => {
    setMenuPack(pack);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!menuPack) return;
    const deletingPackId = menuPack.entityId;
    try {
      await packsApi.delete(deletingPackId);

      // Optimistically remove from current view first.
      setPacks((prev) => prev.filter((p) => p.entityId !== deletingPackId));
      if (selectedPack?.entityId === deletingPackId) {
        setSelectedPack(null);
      }

      // Re-sync from backend to avoid stale local state/race conditions.
      const packsResult = await packsApi.list(includePublic);
      setPacks(packsResult.data || []);

      setDeleteDialogOpen(false);
      handleMenuClose();
    } catch (err) {
      setError('Failed to delete pack');
      console.error(err);
    }
  };

  const handleDownload = async (pack: Pack) => {
    try {
      const result = await packsApi.generate(pack.entityId, 'json');
      const blob = new Blob([JSON.stringify(result.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pack.promptPackName.toLowerCase().replace(/\s+/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download pack');
      console.error(err);
    }
    handleMenuClose();
  };

  const handleCreatePack = async () => {
    try {
      const pack = await packsApi.create({
        name: newPack.name,
        description: newPack.description,
        price: newPack.price,
        tags: newPack.tags.split(',').map((t) => t.trim()).filter(Boolean),
        isPublic: newPack.isPublic,
        promptIds: newPack.selectedPromptIds,
        freeSampleIds: newPack.freeSampleIds,
      });
      setPacks([pack.data, ...packs]);
      setCreateDialogOpen(false);
      setNewPack({
        name: '',
        description: '',
        price: 0,
        tags: '',
        isPublic: false,
        selectedPromptIds: [],
        freeSampleIds: [],
      });
    } catch (err) {
      setError('Failed to create pack');
      console.error(err);
    }
  };

  const togglePromptSelection = (promptId: string) => {
    setNewPack((prev) => ({
      ...prev,
      selectedPromptIds: prev.selectedPromptIds.includes(promptId)
        ? prev.selectedPromptIds.filter((id) => id !== promptId)
        : [...prev.selectedPromptIds, promptId],
    }));
  };

  const toggleFreeSample = (promptId: string) => {
    setNewPack((prev) => ({
      ...prev,
      freeSampleIds: prev.freeSampleIds.includes(promptId)
        ? prev.freeSampleIds.filter((id) => id !== promptId)
        : [...prev.freeSampleIds, promptId],
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Prompt Packs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={includePublic}
                onChange={(e) => setIncludePublic(e.target.checked)}
              />
            }
            label="Include Public"
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import Prompt Pack
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {importSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSuccess(null)}>
          {importSuccess}
        </Alert>
      )}

      {showImportBanner && importedBookId && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setShowImportBanner(false)}
        >
          {importedPackId ? (
            <>
              DynamicBooks import complete. Book{' '}
              <Tooltip title={importedBookId}>
                <strong>{shortId(importedBookId)}</strong>
              </Tooltip>{' '}
              is linked to prompt pack{' '}
              <Tooltip title={importedPackId}>
                <strong>{shortId(importedPackId)}</strong>
              </Tooltip>
              {importedProjectId && (
                <>
                  {' '}
                  in project{' '}
                  <Tooltip title={importedProjectId}>
                    <strong>{shortId(importedProjectId)}</strong>
                  </Tooltip>
                </>
              )}
              {importedPackCount && (
                <>
                  {' '}
                  with <strong>{importedPackCount}</strong> chapter packs
                </>
              )}
              .
            </>
          ) : (
            <>
              DynamicBooks import is in progress for book{' '}
              <Tooltip title={importedBookId}>
                <strong>{shortId(importedBookId)}</strong>
              </Tooltip>
              .
            </>
          )}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search packs..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={3}>
        {filteredPacks.map((pack) => (
          <Grid item xs={12} sm={6} md={4} key={pack.entityId}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" noWrap sx={{ flex: 1 }}>
                    {pack.promptPackName}
                  </Typography>
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, pack)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, height: 40, overflow: 'hidden' }}
                >
                  {pack.description || 'No description'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={pack.isPublic ? <PublicIcon /> : <LockIcon />}
                    label={pack.isPublic ? 'Public' : 'Private'}
                    size="small"
                    variant="outlined"
                  />
                  {pack.price > 0 && (
                    <Chip
                      icon={<AttachMoneyIcon />}
                      label={`${pack.price} ${pack.currency}`}
                      size="small"
                      color="success"
                    />
                  )}
                  <Chip
                    icon={<DescriptionIcon />}
                    label={`${(pack.userPromptIds ?? []).length} prompts`}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(pack.tags ?? []).slice(0, 3).map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                  {(pack.tags ?? []).length > 3 && (
                    <Chip label={`+${(pack.tags ?? []).length - 3}`} size="small" />
                  )}
                </Box>
              </CardContent>

              <CardActions>
                <Button size="small" onClick={() => handleViewDetails(pack)}>
                  View Details
                </Button>
                <Button size="small" onClick={() => handleViewContents(pack)}>
                  Contents
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRequestDelete(pack)}
                  sx={{ ml: 'auto' }}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredPacks.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No packs found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first prompt pack to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Pack
          </Button>
        </Box>
      )}

      {/* Floating Add Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuPack && handleViewContents(menuPack)}>
          <DescriptionIcon sx={{ mr: 1 }} /> View Contents
        </MenuItem>
        <MenuItem onClick={() => {
          setPackToPublish(menuPack);
          setPublishDialogOpen(true);
          handleMenuClose();
        }}>
          <PublishIcon sx={{ mr: 1 }} /> Publish to Store
        </MenuItem>
        <MenuItem onClick={() => menuPack && handleDownload(menuPack)}>
          <DownloadIcon sx={{ mr: 1 }} /> Download
        </MenuItem>
        <MenuItem onClick={() => setDeleteDialogOpen(true)}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* View Pack Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedPack?.promptPackName}</DialogTitle>
        <DialogContent>
          {selectedPack && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedPack.description || 'No description provided'}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price
                  </Typography>
                  <Typography>
                    {selectedPack.price > 0
                      ? `${selectedPack.price} ${selectedPack.currency}`
                      : 'Free'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Visibility
                  </Typography>
                  <Typography>{selectedPack.isPublic ? 'Public' : 'Private'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prompts
                  </Typography>
                  <Typography>{(selectedPack.userPromptIds ?? []).length}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Free Samples
                  </Typography>
                  <Typography>{(selectedPack.freeSampleIds ?? []).length}</Typography>
                </Grid>
              </Grid>

              {(selectedPack.tags ?? []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(selectedPack.tags ?? []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              if (!selectedPack) return;
              setMenuPack(selectedPack);
              setDialogOpen(false);
              setDeleteDialogOpen(true);
            }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => selectedPack && handleDownload(selectedPack)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pack Contents Dialog */}
      <Dialog
        open={contentsDialogOpen}
        onClose={() => setContentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pack Contents</DialogTitle>
        <DialogContent>
          {selectedPack && (
            <List>
              {(selectedPack.userPromptIds ?? []).map((pid) => {
                const prompt = prompts.find((p) => p.entityId === pid);
                return (
                  <ListItem key={pid}>
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={prompt?.promptName || pid}
                      secondary={
                        (selectedPack.freeSampleIds ?? []).includes(pid)
                          ? 'Free Sample'
                          : 'Premium'
                      }
                    />
                    {(selectedPack.freeSampleIds ?? []).includes(pid) && (
                      <Chip label="Free" size="small" color="success" />
                    )}
                  </ListItem>
                );
              })}
              {(selectedPack.userPromptIds ?? []).length === 0 && (
                <ListItem>
                  <ListItemText primary="No prompts in this pack" />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContentsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Pack Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Prompt Pack</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Pack Name"
              fullWidth
              value={newPack.name}
              onChange={(e) => setNewPack({ ...newPack, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={newPack.description}
              onChange={(e) => setNewPack({ ...newPack, description: e.target.value })}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Price"
                  type="number"
                  fullWidth
                  value={newPack.price}
                  onChange={(e) =>
                    setNewPack({ ...newPack, price: parseFloat(e.target.value) || 0 })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Tags (comma-separated)"
                  fullWidth
                  value={newPack.tags}
                  onChange={(e) => setNewPack({ ...newPack, tags: e.target.value })}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={newPack.isPublic}
                  onChange={(e) => setNewPack({ ...newPack, isPublic: e.target.checked })}
                />
              }
              label="Make Public"
            />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1">Select Prompts</Typography>
            <List sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {prompts.map((prompt) => (
                <ListItem
                  key={prompt.entityId}
                  button
                  onClick={() => togglePromptSelection(prompt.entityId)}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={newPack.selectedPromptIds.includes(prompt.entityId)}
                    />
                  </ListItemIcon>
                  <ListItemText primary={prompt.promptName} secondary={prompt.description} />
                  {newPack.selectedPromptIds.includes(prompt.entityId) && (
                    <Tooltip title="Mark as free sample">
                      <Checkbox
                        checked={newPack.freeSampleIds.includes(prompt.entityId)}
                        onChange={() => toggleFreeSample(prompt.entityId)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    </Tooltip>
                  )}
                </ListItem>
              ))}
              {prompts.length === 0 && (
                <ListItem>
                  <ListItemText primary="No prompts available. Create some prompts first." />
                </ListItem>
              )}
            </List>
            <Typography variant="caption" color="text.secondary">
              Check the checkbox on the right to mark a prompt as a free sample
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreatePack}
            disabled={!newPack.name || newPack.selectedPromptIds.length === 0}
          >
            Create Pack
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Pack?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{menuPack?.promptPackName}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish to Store Dialog */}
      <PublishPromptPackDialog
        open={publishDialogOpen}
        onClose={() => {
          setPublishDialogOpen(false);
          setPackToPublish(null);
        }}
        pack={packToPublish}
        onSuccess={(result) => {
          console.log('Published successfully:', result);
        }}
      />

      {/* Import Prompt Pack Dialog */}
      <ImportPromptPackDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        extractFn={packsApi.extractText}
        saveFn={(payload) => packsApi.importText({ ...payload, text: undefined })}
        onImported={async ({ packName, promptsImported }) => {
          setImportSuccess(
            `Imported "${packName}" with ${promptsImported} prompt${promptsImported === 1 ? '' : 's'}.`
          );
          const packsResult = await packsApi.list(includePublic);
          setPacks(packsResult.data || []);
        }}
      />
    </Box>
  );
}
